import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Avatar,
  Chip,
  Card,
  Divider,
  Grid,
  Button,
  Modal,
  Alert,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  fetchCurrentUser,
  changeUserPassword,
  updateUser,
} from "../../redux/usersSlice";
import { getMedusaInspiredColor, getInitials } from "../../utils/utils";
import PhotoUpload from "../Profiles/PhotoUpload";
import { getUserProfilePicture } from "../../services/apiService";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { styled } from "@mui/material/styles";

// Custom Styled TextField with rounded corners and smooth transitions
const CustomTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: theme.shape.borderRadius * 1.5,
    transition: "all 0.3s ease",
    "&:hover fieldset": {
      borderColor: theme.palette.primary.main,
    },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.primary.dark,
    },
  },
}));

// Enhanced Background Card with gradient and hover effect
const EnhancedCard = styled(Card)(({ theme }) => ({
  maxWidth: 800,
  margin: "auto",
  marginTop: theme.spacing(5),
  padding: theme.spacing(3),
  borderRadius: theme.spacing(3),
  background: "linear-gradient(135deg, #f9f9f9 0%, #ebedee 100%)",
  boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
}));

// Enhanced Modal Box with gradient background and rounded corners
const EnhancedModalBox = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  background: "linear-gradient(135deg, #ffffff, #bfddeb)",
  borderRadius: theme.spacing(2),
  boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
  padding: theme.spacing(4),
}));

// New styled component for a larger Profile Modal
const EnhancedProfileModalBox = styled(EnhancedModalBox)(({ theme }) => ({
  width: 600, // Increased width for the profile modal
}));

const Profile = () => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.users.currentUser);
  const error = useSelector((state) => state.users.error);
  const [openPasswordModal, setOpenPasswordModal] = useState(false);
  const [openProfileModal, setOpenProfileModal] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [success, setSuccess] = useState("");
  const [profileData, setProfileData] = useState({
    fullName: "",
    address: "",
    phone: "",
    birthDate: "",
    countryCode: "",
  });
  const [profilePicture, setProfilePicture] = useState(null);
  const [profilePictureUrl, setProfilePictureUrl] = useState(null);
  const [imageError, setImageError] = useState("");

  useEffect(() => {
    dispatch(fetchCurrentUser());
  }, [dispatch]);

  useEffect(() => {
    if (currentUser) {
      setProfileData({
        fullName: currentUser.fullName,
        address: currentUser.address,
        phone: currentUser.phone,
        birthDate: currentUser.birthDate,
      });
      if (currentUser.profilePicture) {
        getUserProfilePicture(currentUser.profilePicture)
          .then((url) => setProfilePictureUrl(url))
          .catch((error) =>
            console.error("Failed to load profile picture:", error)
          );
      }
    }
  }, [currentUser]);

  const handleChangePassword = async () => {
    try {
      await dispatch(
        changeUserPassword({ id: currentUser.id, oldPassword, newPassword })
      ).unwrap();
      setOpenPasswordModal(false);
      setOldPassword("");
      setNewPassword("");
      setSuccess("Mot de passe mis à jour avec succès");
    } catch (err) {
      // Error handled by Redux slice
    }
  };

  const handleUpdateProfile = async () => {
    try {
      const data = {
        fullName: profileData.fullName,
        address: profileData.address,
        phone: profileData.phone,
        birthDate: profileData.birthDate,
      };
      const response = await dispatch(
        updateUser({ id: currentUser.id, data, profilePicture })
      ).unwrap();
      setOpenProfileModal(false);
      setSuccess("Profil mis à jour avec succès");
      if (response.user.profilePicture) {
        const url = await getUserProfilePicture(response.user.profilePicture);
        setProfilePictureUrl(url);
      } else {
        setProfilePictureUrl(null);
      }
      dispatch(fetchCurrentUser());
    } catch (err) {
      // Error handled by Redux slice
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setImageError("Le fichier ne doit pas dépasser 5 Mo.");
        return;
      }
      if (!["image/jpeg", "image/png", "image/jpg"].includes(file.type)) {
        setImageError("Le fichier doit être une image jpg/png/jpeg.");
        return;
      }
      setProfilePicture(file);
      setImageError("");
    }
  };

  // Helper to always return a simple error message for password/profile errors
  const getErrorMessage = (error) => {
    if (!error) return "";
    // Always show the same message for password errors in the password modal
    if (openPasswordModal) {
      return "Ancien mot de passe invalide.";
    }
    // Otherwise, generic message for profile errors
    return "Une erreur s'est produite.";
  };

  if (!currentUser) {
    return null;
  }

  return (
    <EnhancedCard>
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}
      {/* Remove global error Alert for password errors */}
      {/* Only show error Alert inside the password modal */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 3,
          mb: 3,
          background: "linear-gradient(135deg, #ffffff, #d1d9e6)", // Updated background
          p: 2,
          borderRadius: 2,
        }}
      >
        <Avatar
          src={profilePictureUrl}
          sx={{
            width: 100,
            height: 100,
            backgroundColor: profilePictureUrl
              ? "transparent"
              : getMedusaInspiredColor(currentUser.username),
            color: "#fff",
            fontSize: "2.5rem",
            fontWeight: "bold",
          }}
        >
          {!profilePictureUrl && getInitials(currentUser.username)}
        </Avatar>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            {currentUser.username}
          </Typography>
          <Typography variant="body1" color="textSecondary">
            {currentUser.email}
          </Typography>
          <Button
            variant="contained"
            onClick={() => setOpenPasswordModal(true)}
            sx={{
              mt: 2,
              borderRadius: "50px",
              backgroundColor: "#2F4E8B", // Updated color
              color: "#fff",
              fontWeight: "bold",
              "&:hover": {
                backgroundColor: "#2F4E8B", // Updated hover color
              },
            }}
          >
            Changer Mot de passe
          </Button>
          <Button
            variant="contained"
            onClick={() => setOpenProfileModal(true)}
            sx={{
              mt: 2,
              ml: 2,
              borderRadius: "50px",
              backgroundColor: "#2F4E8B", // Updated color
              color: "#fff",
              fontWeight: "bold",
              "&:hover": {
                backgroundColor: "#2F4E8B", // Updated hover color
              },
            }}
          >
            Modifier Profil
          </Button>
        </Box>
      </Box>

      <Divider sx={{ mb: 2 }} />

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Typography variant="h6" fontWeight="bold">
            Nom complet
          </Typography>
          <Typography variant="body1">{currentUser.fullName}</Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="h6" fontWeight="bold">
            Adresse
          </Typography>
          <Typography variant="body1">{currentUser.address}</Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="h6" fontWeight="bold">
            Téléphone
          </Typography>
          <PhoneInput
            country={"tn"}
            value={currentUser.phone}
            disabled
            containerStyle={{
              width: "100%",
              backgroundColor: "transparent",
              border: "none",
            }}
            inputStyle={{
              width: "100%",
              backgroundColor: "transparent",
              border: "none",
              color: "#000",
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="h6" fontWeight="bold">
            Date de naissance
          </Typography>
          <Typography variant="body1">{currentUser.birthDate}</Typography>
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      {currentUser.isPremium ? (
        <Chip
          label="Utilisateur Premium"
          sx={{
            backgroundColor: "#ffd700",
            color: "#000",
            fontWeight: "bold",
            px: 2,
            py: 0.5,
          }}
        />
      ) : (
        <Chip
          label="Utilisateur Non Premium"
          sx={{
            backgroundColor: "#d32f2f",
            color: "#fff",
            fontWeight: "bold",
            px: 2,
            py: 0.5,
          }}
        />
      )}

      {/* Password Modal */}
      <Modal open={openPasswordModal}>
        <EnhancedModalBox
          sx={{
            background: "linear-gradient(135deg, #ffffff, #d1d9e6)", // Updated background
          }}
        >
          <Box sx={{ position: "absolute", top: 8, right: 8 }}>
            <CloseIcon
              onClick={() => setOpenPasswordModal(false)}
              sx={{ color: "#2F4E8B", cursor: "pointer" }} // Blue close icon
            />
          </Box>
          <CustomTextField
            label="Ancien Mot de passe"
            type="password"
            fullWidth
            value={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)}
            sx={{ mb: 2, backgroundColor: "#FFFFFF" }}
          />
          <CustomTextField
            label="Nouveau Mot de passe"
            type="password"
            fullWidth
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            sx={{ mb: 2, backgroundColor: "#FFFFFF" }}
          />
          {/* Show error as Alert only inside the modal */}
          {error && openPasswordModal && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {getErrorMessage(error)}
            </Alert>
          )}
          <Button
            variant="contained"
            onClick={handleChangePassword}
            sx={{
              display: "block",
              mx: "auto",
              backgroundColor: "#2F4E8B", // Updated color
              color: "#fff",
              fontWeight: "bold",
              borderRadius: "20px",
              "&:hover": {
                backgroundColor: "#2F4E8B", // Updated hover color
              },
            }}
          >
            Soumettre
          </Button>
        </EnhancedModalBox>
      </Modal>

      {/* Profile Modal */}
      <Modal open={openProfileModal}>
        <EnhancedProfileModalBox
          sx={{
            background: "linear-gradient(135deg, #ffffff, #d1d9e6)", // Updated background
          }}
        >
          <Box sx={{ position: "absolute", top: 8, right: 8 }}>
            <CloseIcon
              onClick={() => setOpenProfileModal(false)}
              sx={{ color: "#2F4E8B", cursor: "pointer" }} // Blue close icon
            />
          </Box>
          <CustomTextField
            label="Nom complet"
            fullWidth
            value={profileData.fullName}
            onChange={(e) =>
              setProfileData({ ...profileData, fullName: e.target.value })
            }
            sx={{ mb: 2, backgroundColor: "#FFFFFF" }}
          />
          <CustomTextField
            label="Adresse"
            fullWidth
            value={profileData.address}
            onChange={(e) =>
              setProfileData({ ...profileData, address: e.target.value })
            }
            sx={{ mb: 2, backgroundColor: "#FFFFFF" }}
          />
          <PhoneInput
            country={"fr"}
            value={profileData.phone}
            onChange={(value, country) => {
              setProfileData({
                ...profileData,
                phone: value,
                countryCode: country.dialCode,
              });
            }}
            inputStyle={{
              width: "100%",
              backgroundColor: "#FFFFFF",
              borderRadius: "4px",
              borderColor: "rgba(0, 0, 0, 0.23)",
              height: "56px",
            }}
            containerStyle={{
              marginBottom: "16px", // Add space below the phone input
            }}
            inputProps={{
              name: "phone",
              required: true,
            }}
          />
          <CustomTextField
            label="Date de naissance"
            type="date"
            fullWidth
            value={profileData.birthDate}
            onChange={(e) =>
              setProfileData({ ...profileData, birthDate: e.target.value })
            }
            sx={{ mb: 3, backgroundColor: "#FFFFFF" }}
            InputLabelProps={{
              shrink: true,
            }}
          />
          <PhotoUpload
            imageFile={profilePicture}
            handleImageChange={handleImageChange}
            imageError={imageError}
          />
          {/* Only show error if not from password modal */}
          {error && !openPasswordModal && (
            <Typography color="error" sx={{ mb: 2 }}>
              {getErrorMessage(error)}
            </Typography>
          )}
          <Button
            variant="contained"
            onClick={handleUpdateProfile}
            sx={{
              display: "block",
              mx: "auto",
              backgroundColor: "#2F4E8B", // Updated color
              color: "#fff",
              fontWeight: "bold",
              borderRadius: "20px",
              "&:hover": {
                backgroundColor: "#2F4E8B", // Updated hover color
              },
            }}
          >
            Soumettre
          </Button>
        </EnhancedProfileModalBox>
      </Modal>
    </EnhancedCard>
  );
};

export default Profile;
